// 这个文件主要是用来处理请求的数据

import { ParamsTimeType } from '@op/types/common';
import moment, { unitOfTime } from 'moment';
import {
  getPercentageValue,
  getRationPercentValue,
  getTimeSpaceArray,
  sortMapAuxiliaryFunc,
  uniqueArray,
} from '.';
import { DBO_ARR, DBO_TM } from './format';
import { dealwithAbsValue } from './value';

const dateTypeMap: { [key in ParamsTimeType]: unitOfTime.Diff } = {
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  hourly: 'hour',
};

export type TRequestBackType = {
  series?: Array<{ name: string; data: number[] }>;
  seriesObj?: { [key: string]: number[] };
  xAxis?: Array<string>;
  legend?: Array<string>;
  isEmpty?: boolean;
  totalSeries?: Array<number>;
  tableRecord?: Array<any>;
  isMin?: boolean;
};

export type TRequestPieBackType = {
  series?: Array<{ name: string; value: number }>;
  legend?: Array<string>;
  isEmpty?: boolean;
  totalSeries?: number;
};

interface IMoreKeysListType {
  timeline: Array<any>;
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  filter: { date_type: ParamsTimeType };
  addCountValue?: (item: any) => { [key: string]: any };
  timeKey?: string;
  zeroIsNull?: boolean;
  sortByBeforeLine?: boolean;
  sortFunc?: (before: string, after: string) => number;
  totalKey?: string;
  needTable?: boolean;
  moreLineTable?: boolean;
  fixedKeyList?: Array<string>;
  otherIsEnd?: boolean;
  timeList?: Array<string>;
  minuteTimeSpace?: number;
  /** 是否用完整时间轴补全表格数据（缺失日期补 0） */
  fillTableDates?: boolean;
}
/**
 * 处理多key的列表，后端返回数据如下
 * { 
        "timeline": [
            {
                "tiktok": 39,
                "time": "2023-10-24 00:00:00",
                "youtube": 52,
                "twitter": 84
            },
            // ..... 
        ],
        "timeline_of_start_time": "2023-10-11 00:00:00",
        "timeline_of_end_time": "2023-10-28 00:00:00"
    }
 * @param timeline 数据列表
 * @param timeline_of_start_time  开始时间 
 * @param timeline_of_end_time    结束时间
 * @param addCountValue    添加计算值
 * @param filter    过滤器条件，至少要有date_type属性
 * @param timeKey   timeline数据中时间的key是哪个
 * @param needTable  是否需要表格数据
 * @param moreLineTable  是否需要多列表格数据
 * @param fixedKeyList  固定key列表, 可以通过前台传入
 * 
 */
export function dealwithMoreKeysList({
  timeline,
  timeline_of_start_time,
  timeline_of_end_time,
  addCountValue,
  filter,
  timeKey = 'time',
  zeroIsNull = false,
  sortByBeforeLine = true,
  sortFunc,
  totalKey = 'total',
  needTable = false,
  moreLineTable = false,
  fixedKeyList,
  otherIsEnd = true,
  timeList,
  minuteTimeSpace,
  fillTableDates = false,
}: IMoreKeysListType): TRequestBackType {
  function addCountValueFunc(item: any) {
    if (addCountValue) {
      return addCountValue(item);
    }
    return {};
  }

  const { date_type } = filter;
  const dateType = dateTypeMap[date_type];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

  const timelineObj: { [key: string]: any } = {};
  let keys: Array<string> = [totalKey, ...(fixedKeyList || [])];
  // 补充表格数据
  const tableRecord: Array<any> = [];

  // 如果给了fixedKeyList，并且不需要table数据，那么就不走下面的循环
  // 1. 将数据转化为对象，并获取到对象中所有的key，为后续补充数据做准备
  timeline?.forEach(item => {
    const time = item[timeKey];
    const realKey = moment(time).format(format);

    let total = 0;
    const keyList: Array<string> = [];

    Object.keys(item || {}).forEach(key => {
      if (key === timeKey) return;

      const value = dealwithAbsValue(item[key]);

      keyList.push(key);
      total += value;

      if (needTable && !moreLineTable) {
        // 补充表格数据
        tableRecord.push({ time: item[timeKey], key, value });
      }
    });

    const data = { ...item, [totalKey]: total, ...addCountValueFunc(item) };
    timelineObj[realKey] = data;
    if (!fillTableDates && needTable && moreLineTable && data[timeKey]) {
      // 补充表格数据（fillTableDates 模式下跳过，在步骤3统一生成）
      tableRecord.push(data);
    }

    if (!fixedKeyList) {
      keys.push(...keyList);
    }
  });

  // fillTableDates 模式下，排序在步骤3之后统一执行

  // 2. 获取到所有的时间轴，为补充数据做准备
  let dateArr = timeList || [];

  if (dateArr.length === 0) {
    if (minuteTimeSpace) {
      dateArr = DBO_TM.minuteTimeSpace({
        start_time: timeline_of_start_time,
        end_time: timeline_of_end_time,
        interval: minuteTimeSpace,
      });
    } else {
      dateArr = getTimeSpaceArray(
        timeline_of_start_time,
        timeline_of_end_time,
        dateType,
        format,
      );
    }
  }
  keys = uniqueArray(keys);
  const seriesObj: { [key: string]: number[] } = {};
  let beforeLine = {};
  // 3. 补充数据
  dateArr.forEach((date, index) => {
    // 3.1 拿到数据
    const data = timelineObj[date] || {};
    // 3.2 如果数据不存在，就补充数据
    const dataItem = keys.reduce((pre, key) => {
      if (!seriesObj[key]) {
        seriesObj[key] = [];
      }
      const value = data[key] || 0;
      const realValue = zeroIsNull && value === 0 ? null : value;
      seriesObj[key].push(realValue);

      return { ...pre, [key]: value };
    }, {});

    // fillTableDates 模式下，在完整时间轴遍历中补充表格数据
    if (fillTableDates && needTable) {
      if (moreLineTable) {
        tableRecord.push({ ...dataItem, [timeKey]: date });
      } else {
        keys.forEach(k => {
          if (k !== totalKey) {
            tableRecord.push({
              [timeKey]: date,
              key: k,
              value: (dataItem as Record<string, number>)[k] || 0,
            });
          }
        });
      }
    }

    if (index === 0) {
      beforeLine = dataItem;
    }
  });

  // 表格数据排序
  if (tableRecord.length) {
    tableRecord.sort(DBO_ARR.sortByTime(timeKey, 'desc'));
  }

  // 4.进行排序, 默认通过第一列数据进行排序
  if (sortByBeforeLine && !sortFunc) {
    keys.sort(sortMapAuxiliaryFunc(beforeLine, item => item, 'desc'));
  }

  if (sortFunc) {
    keys.sort(sortFunc);
  }

  if (otherIsEnd) {
    keys.sort((a: string, b: string) => {
      const keyA = a.toLocaleLowerCase();
      const keyB = b.toLocaleLowerCase();
      if (['other', 'others'].includes(keyA)) return 1;
      if (['other', 'others'].includes(keyB)) return -1;
      return 0;
    });
  }

  let totalSeries: Array<number> = [];
  const series = keys
    .map(key => {
      const data = seriesObj[key] ?? [];

      if (key === totalKey) {
        totalSeries = data;
        return null;
      }

      return { name: key, data };
    })
    .filter(item => item !== null);

  const isEmpty = totalSeries.every(item => !Boolean(item));

  // 5. 输出
  return {
    series,
    seriesObj,
    xAxis: dateArr,
    legend: keys.filter(item => item !== totalKey),
    isEmpty,
    isMin: Math.max(...(seriesObj?.total || [])) < 0.1,
    totalSeries,
    tableRecord,
  };
}

type TKeyListValueType = {
  timeline: Array<any>;
  timeKey?: string;
  valueKey?: string;
  key?: string;
};
// 其他接口参数，可以考虑转成上面的数据结构，然后通过dealwithMoreKeysList处理
export function dealwithKeyListValue(
  {
    timeline,
    timeKey = 'time',
    valueKey = 'value',
    key = 'key',
  }: TKeyListValueType,
  props: Omit<IMoreKeysListType, 'timeline' | 'timeKey'>,
): TRequestBackType {
  function transformDataWithMap(data: Array<any>) {
    const timeMap = new Map<string, { [key: string]: any }>();
    (data || []).forEach((item: any) => {
      const time = item[timeKey];
      const value = item[valueKey];
      const realKey = item[key];

      // 固定时间key为time了
      if (!timeMap.has(time)) {
        timeMap.set(time, { time });
      }
      const timeObj = timeMap.get(time)!;
      timeObj[realKey] = value;
    });

    return Array.from(timeMap.values());
  }

  const list = transformDataWithMap(timeline);

  return dealwithMoreKeysList({
    timeline: list,
    ...props,
    timeKey: 'time',
  });
}

type CalculateDataType = Record<string, number>;
type KeySuffixType = string | ((key: string) => string);

function getValueBigZero(value: number, valueBigZero: boolean) {
  if (typeof value !== 'number' || isNaN(value)) {
    return 0;
  }

  if (valueBigZero && value < 0) {
    return 0;
  }

  return value;
}

type CalculateFunc = (value: {
  item: CalculateDataType;
  otherItem: CalculateDataType;
  keySuffix: KeySuffixType;
  valueBigZero?: boolean;
  precision?: number;
}) => (key: string) => Record<string, number>;

/**
 * 创建计算同比变化的函数
 * @param item    新的对象
 * @param oldItem 老的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
const getCalculateQOQFunc: CalculateFunc = ({
  item,
  otherItem,
  keySuffix,
  valueBigZero = true,
  precision = 2,
}) => {
  return (key: string) => {
    const newValue = getValueBigZero(item?.[key], valueBigZero);
    const oldValue = getValueBigZero(otherItem?.[key], valueBigZero);
    const value = getRationPercentValue(newValue, oldValue, precision);
    // 获取真实key值
    const realKey =
      typeof keySuffix === 'string' ? key + keySuffix : keySuffix(key);
    return { [realKey]: value, [key]: newValue, [`${key}_old`]: oldValue };
  };
};

/**
 * 创建计算占比变化的函数
 * @param item    新的对象
 * @param totalItem 总数的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
const getCalculateRatioFunc: CalculateFunc = ({
  item,
  otherItem,
  keySuffix,
  valueBigZero = true,
  precision = 2,
}: {
  item: CalculateDataType;
  otherItem: CalculateDataType;
  keySuffix: KeySuffixType;
  valueBigZero?: boolean;
  precision?: number;
}) => {
  return (key: string) => {
    const newValue = getValueBigZero(item?.[key], valueBigZero);
    const totalValue = getValueBigZero(otherItem?.[key], valueBigZero);
    const value = getPercentageValue(newValue, totalValue, precision);
    // 获取真实key值
    const realKey =
      typeof keySuffix === 'string' ? key + keySuffix : keySuffix(key);
    return { [realKey]: value, [key]: newValue, [`${key}_total`]: totalValue };
  };
};

type TQOQAndRatioPropsType = {
  list: CalculateDataType;
  otherList: CalculateDataType;
  keys?: Array<string>;
  excludeKeys?: Array<string>;
  valueBigZero?: boolean;
  keySuffix?: KeySuffixType;
  precision?: number;
};

function dealwithCommonData({
  list,
  otherList,
  keys = [],
  excludeKeys = [],
  valueBigZero = true,
  keySuffix = '',
  precision = 2,
  calculateFunc,
}: TQOQAndRatioPropsType & {
  calculateFunc: CalculateFunc;
}) {
  const newData = list || {};
  const otherData = otherList || {};

  let calculateKeys = keys;
  if (calculateKeys.length === 0) {
    calculateKeys = Array.from(
      new Set([...Object.keys(newData), ...Object.keys(otherData)]),
    );
    if (excludeKeys.length !== 0) {
      calculateKeys = calculateKeys.filter(item => !excludeKeys.includes(item));
    }
  }

  if (calculateKeys.length === 0) {
    return {};
  }

  const calculate = calculateFunc({
    item: newData,
    otherItem: otherData,
    keySuffix,
    valueBigZero,
    precision,
  });
  const newDataObj = calculateKeys.reduce((pre, key) => {
    const value = calculate(key);
    return {
      ...pre,
      ...value,
    };
  }, {});

  return newDataObj;
}

export function dealwithQOQ(props: TQOQAndRatioPropsType) {
  return dealwithCommonData({
    keySuffix: '_qoq',
    ...props,
    calculateFunc: getCalculateQOQFunc,
  });
}

export function dealwithRatio(props: TQOQAndRatioPropsType) {
  return dealwithCommonData({
    keySuffix: '_ratio',
    ...props,
    calculateFunc: getCalculateRatioFunc,
  });
}

/**
 * 数据有newList 和 oldList的情况
 * @param list
 * @param options
 * @returns
 */
export function dealwithTableData(
  list: Array<any>,
  options: {
    hasQOQ: boolean;
    hasRATIO: boolean;
    qoqProps?: Omit<TQOQAndRatioPropsType, 'list' | 'otherList'>;
    ratioProps?: Omit<TQOQAndRatioPropsType, 'list' | 'otherList'>;
    newListKey: string;
    oldListKey?: string;
    totalMap?: Record<string, number>;
  },
) {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  const {
    hasQOQ,
    hasRATIO,
    qoqProps,
    ratioProps,
    newListKey,
    oldListKey,
    totalMap,
  } = options;

  const newList = list.map(item => {
    let qoqData = {};
    let ratioData = {};

    if (hasQOQ && oldListKey) {
      qoqData = dealwithQOQ({
        list: item[newListKey],
        otherList: item[oldListKey],
        ...qoqProps,
      });
    }

    if (hasRATIO && totalMap) {
      ratioData = dealwithRatio({
        list: item[newListKey],
        otherList: totalMap,
        ...ratioProps,
      });
    }

    return {
      ...item,
      ...qoqData,
      ...ratioData,
    };
  });

  return newList;
}

/**
 * 所有数据直接平铺的情况
 * @param list
 * @param options
 * @returns
 */
export function dealwithSingleTableData(
  list: Array<any>,
  options: {
    hasQOQ: boolean;
    hasRATIO: boolean;
    totalMap?: Record<string, number>;
    keys: Array<string>;
    oldKeys?: Array<string>;
    qoqProps?: Omit<TQOQAndRatioPropsType, 'list' | 'otherList'>;
    ratioProps?: Omit<TQOQAndRatioPropsType, 'list' | 'otherList'>;
  },
) {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  const { hasQOQ, hasRATIO, keys, oldKeys, totalMap, qoqProps, ratioProps } =
    options;

  const newList = list.map(item => {
    let qoqData = {};
    let ratioData = {};
    let oldList: null | Record<string, any> = null;

    const newList = keys.reduce((pre, key) => {
      return { ...pre, [key]: item[key] };
    }, {});
    if (oldKeys) {
      oldList = oldKeys.reduce((pre, key) => {
        return { ...pre, [key]: item[key] };
      }, {});
    }

    if (hasQOQ && oldList) {
      qoqData = dealwithQOQ({
        list: newList,
        otherList: oldList,
        ...qoqProps,
      });
    }

    if (hasRATIO && totalMap) {
      ratioData = dealwithRatio({
        list: newList,
        otherList: totalMap,
        ...ratioProps,
      });
    }

    return {
      ...item,
      ...qoqData,
      ...ratioData,
    };
  });

  return newList;
}
